"use client";

import { useUrlSearchParams } from "@trydig/ui/next";
import { useCallback, useMemo } from "react";

const SORT_PARAM = "sort";
const PAGE_PARAM = "page";

const RESERVED_PARAMS = new Set(["modal", SORT_PARAM, PAGE_PARAM]);

// Single-char separator that stays unencoded in URLs and never appears in
// Depict's JSON-stringified multi-color values like `["Brown","White"]`.
// Reading the URL is just `?color=Red|Blue|Green` — one param per filter key.
const VALUE_DELIMITER = "|";

const encodeValues = (values: string[]): string => values.join(VALUE_DELIMITER);
const decodeValues = (raw: string | null): string[] =>
	raw ? raw.split(VALUE_DELIMITER).filter(Boolean) : [];

export type FilterState = {
	filters: Record<string, string[]>;
	sortId: string | undefined;
};

/**
 * Filter + sort state lives in the URL. Each filter key maps to a single
 * pipe-delimited query param (e.g. `?color=Red|Blue&size=M|L`). Sort is a
 * single `?sort=id` param.
 *
 * `knownFilterKeys` is the set of keys the current page data exposes. Reading
 * only those keys keeps the hook stable when other features write unrelated
 * params (modal, page, etc.).
 */
export const usePLPFilters = (knownFilterKeys: string[]) => {
	const searchParams = useUrlSearchParams({ replace: true, scroll: false });
	const knownSet = useMemo(() => new Set(knownFilterKeys), [knownFilterKeys]);

	const filters = useMemo<Record<string, string[]>>(() => {
		const out: Record<string, string[]> = {};
		for (const key of knownSet) {
			const values = decodeValues(searchParams.get(key));
			if (values.length > 0) out[key] = values;
		}
		return out;
	}, [searchParams, knownSet]);

	const sortId = searchParams.get(SORT_PARAM) ?? undefined;

	const setFilter = useCallback(
		(key: string, values: string[]) => {
			if (values.length === 0) searchParams.delete(key);
			else searchParams.set(key, encodeValues(values));
			// Filtering changes the result set; deep-link page becomes meaningless.
			searchParams.delete(PAGE_PARAM);
		},
		[searchParams],
	);

	const toggleFilter = useCallback(
		(key: string, value: string) => {
			// Read from window.location.search rather than the snapshotted
			// `searchParams` closure. The trydig URL hook uses shallow
			// `history.replaceState`, which doesn't always force Next.js's
			// `useSearchParams` to re-render before the next click handler fires.
			// When that happens, this callback's closure still holds the URL state
			// from *two clicks ago* — so a second rapid click would read empty,
			// compute `next = [newValue]`, and overwrite the first click's value
			// instead of appending. Reading live params side-steps the staleness.
			const raw =
				typeof window !== "undefined"
					? new URLSearchParams(window.location.search).get(key)
					: searchParams.get(key);
			const current = decodeValues(raw);
			const next = current.includes(value)
				? current.filter((v) => v !== value)
				: [...current, value];
			setFilter(key, next);
		},
		[searchParams, setFilter],
	);

	const setSort = useCallback(
		(id: string | undefined) => {
			if (id) searchParams.set(SORT_PARAM, id);
			else searchParams.delete(SORT_PARAM);
			searchParams.delete(PAGE_PARAM);
		},
		[searchParams],
	);

	const clear = useCallback(() => {
		const keys: string[] = [];
		searchParams.forEach((_, k) => {
			if (!RESERVED_PARAMS.has(k)) keys.push(k);
		});
		for (const k of keys) searchParams.delete(k);
		searchParams.delete(PAGE_PARAM);
	}, [searchParams]);

	const isFiltered = Object.keys(filters).length > 0 || Boolean(sortId);

	return { filters, sortId, setFilter, toggleFilter, setSort, clear, isFiltered };
};
